import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ACTIVE_API_BASE_URL } from "../config/apiConfig";
import apiClient from "../api/axiosClient";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/FormProduction.css";
import SidebarGuideline from "./SidebarGuideline";
import TextField from "@mui/material/TextField";

import {downloadFile} from '../common'

// Constants for max lengths
const FIELD_MAX_LENGTH = 100;
const COMPANY_PROFILE_MAX_LENGTH = 300;
const COMMENT_MAX_LENGTH = 200;
const PHONE_MAX_LENGTH = 10;

// Validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

const RegistrationProductionlessMMTOE = () => {
  const location = useLocation();
  const [activeItem, setActiveItem] = useState(null);
  const navigate = useNavigate();
  const awardTitle =
    location.state?.awardTitle ||
    "Oil & Gas Production Company of the Year (< 1 MMTOE)";
  const [userInfo, setUserInfo] = useState();
  const [formId, setFormId] = useState("");
  const [PkId, setPkId] = useState(0);

  // Form state initialization
  const [formData, setFormData] = useState({
    Organisationname: "",
    category: "Oil & Gas Production Company of the Year (< 1 MMTOE)",
    mailingAddress: "",
    authorityName: "",
    authorityTitle: "",
    authorityPhone: "",
    authorityEmail: "",
    applicant_name: "",
    applicant_phone: "",
    authorityLandline: "",
    applicant_email: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    companyProfile: "",
    awardJustification: "",
    approvingAuthoritySignature: null,
    declaration: false,
    comment: "",
    "1_totalOil": ["", ""],
    "2_totalGas": ["", ""],
    "3_costPerBOE": ["", ""],
    "4_iorEorCapex": ["", ""],
    "5_totalEnergy": ["", ""],
    "5.1_companyEnergy": ["", ""],
    "5.2_totalCapex": ["", ""],
    "5.3_totalOpex": ["", ""],
    "5.4_productionCapex": ["", ""],
    "5.5_productionOpex": ["", ""],
    "6_co2Emission": ["", ""],
    "8_fatalities": ["", ""],
    "9_lostTimeInjuries": ["", ""],
    "10_oshaIncidents": ["", ""],
    "11_ownManHours": ["", ""],
    "12_contractManHours": ["", ""],
    project1_name: "",
    project1_boardApprovalDate: "",
    project1_startDate: "",
    project1_completionDate: "",
    project1_capex: "",
    project1_remarks: "",
    project2_name: "",
    project2_boardApprovalDate: "",
    project2_startDate: "",
    project2_completionDate: "",
    project2_capex: "",
    project2_remarks: "",
    project3_name: "",
    project3_boardApprovalDate: "",
    project3_startDate: "",
    project3_completionDate: "",
    project3_capex: "",
    project3_remarks: "",
    project4_name: "",
    project4_boardApprovalDate: "",
    project4_startDate: "",
    project4_completionDate: "",
    project4_capex: "",
    project4_remarks: "",
    project5_name: "",
    project5_boardApprovalDate: "",
    project5_startDate: "",
    project5_completionDate: "",
    project5_capex: "",
    project5_remarks: "",
    attachments1: { description: "", file: null },
    attachments2: { description: "", file: null },
    attachments3: { description: "", file: null },
    attachments4: { description: "", file: null },
  });

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("user_info");
      if (stored) {
        const user = JSON.parse(stored);
        setUserInfo(JSON.parse(stored));
        setFormId(`Production_Less_${user.id}`);
        setFormData((prev) => ({
          ...prev,
          userid: user.id || "",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company_name: user.organisation_name || "",
          Organisationname: user.organisation_name || "", // prefill organisation_name field
          // Fill applicant_* only (we DON'T prefill contact_* here)
          applicant_name: user.applicant_name || "",
          applicant_phone: user.applicant_phone || "",
          applicant_email: user.email || "",
        }));
      }
    } catch (err) {
      console.warn("Failed to parse user_info from localStorage:", err);
    }
  }, []);

  // This use Effect Prefill user Data using api;
  useEffect(() => {
    if (userInfo && formId) {
      prefillFormData();
    } else {
      console.warn(
        `userInfo OR formId is invalid; Kindly check the method. and data`
      );
    }
  }, [userInfo, formId]);

  const isSubmited = () => formData.form_mode === "submit";

  async function prefillFormData() {
    try {
      if (!userInfo || !userInfo.id) {
        console.warn("prefillFormData: userInfo.id not available");
        return;
      }

      const url = `/production/?userid=${userInfo.id}&form_id=${formId}`;
      const res = await apiClient.get(url);

      if (!Array.isArray(res.data) || res.data.length !== 1) {
        console.log("No prefill data found:", res.data);
        return;
      }

      const _ResData = res.data[0];
      setPkId(_ResData.id || 0);

      // Helper to pick first present field variant
      const pick = (...keys) => {
        for (const k of keys) {
          if (k in _ResData && _ResData[k] != null) return _ResData[k];
        }
        return undefined;
      };

      // Build newFields
      const newFields = {};

      // Simple fields (try multiple candidate keys from backend)
      newFields.Organisationname =
        pick(
          "organisation_name",
          "organisationName",
          "company_name",
          "companyName"
        ) || "";
      newFields.category = pick("category") || "";
      newFields.mailingAddress =
        pick("mailing_address", "mailingAddress") || "";
      newFields.authorityName = pick("authority_name", "authorityName") || "";
      newFields.authorityTitle =
        pick("authority_title", "authorityTitle") || "";
      newFields.authorityPhone =
        pick("authority_phone", "authorityPhone") || "";
      newFields.authorityLandline =
        pick("authority_landline", "authorityLandline") || "";
      newFields.authorityEmail =
        pick("authority_email", "authorityEmail") || "";
      newFields.applicant_name =
        pick("applicant_name", "contact_name", "applicantName") || "";
      newFields.applicant_phone =
        pick("applicant_phone", "contact_phone", "applicantPhone") || "";
      newFields.applicant_email =
        pick("applicant_email", "contact_email", "applicantEmail") || "";
      newFields.contact_name = pick("contact_name") || "";
      newFields.contact_phone = pick("contact_phone") || "";
      newFields.contact_email = pick("contact_email") || "";
      newFields.companyProfile =
        pick("company_profile", "companyProfile") || "";
      newFields.awardJustification =
        pick("award_justification", "awardJustification") || "";
      newFields.comment = pick("comment") || "";

      // Flat fields used in your FormData mapping if present
      newFields.Firstname = pick("firstname", "first_name") || "";
      newFields.Lastname = pick("lastname", "last_name") || "";
      newFields.userid = pick("userid", "user_id", "userId") || "";
      newFields.companyName =
        pick("company_name", "companyName", "organisation_name") || "";

      // Declaration boolean
      const declVal = pick("declaration");
      newFields.declaration =
        declVal === true ||
        declVal === "true" ||
        declVal === "1" ||
        declVal === 1
          ? true
          : false;

      // form_mode
      newFields.form_mode = pick("form_mode", "formMode") || "draft";

      // approving authority signature: keep file null, provide url if backend returns it
      newFields.approvingAuthoritySignature = null;
      newFields.approvingAuthoritySignatureUrl =
        pick(
          "approving_authority_file",
          "approving_authority_file_url",
          "approvingAuthorityFileUrl",
          "approvingAuthorityFile"
        ) || null;

      // ─── Quantitative pairs mapping (two values: 2024, 2023)
      const quantMap = [
        ["1_totalOil", "total_oil"],
        ["2_totalGas", "total_gas"],
        ["3_costPerBOE", "cost_per_boe"],
        ["4_iorEorCapex", "ior_eor_capex"],
        ["5_totalEnergy", "total_energy"],
        ["5.1_companyEnergy", "company_energy"],
        ["5.2_totalCapex", "total_capex"],
        ["5.3_totalOpex", "total_opex"],
        ["5.4_productionCapex", "production_capex"],
        ["5.5_productionOpex", "production_opex"],
        ["6_co2Emission", "co2_emission"],
        ["8_fatalities", "fatalities"],
        ["9_lostTimeInjuries", "lost_time_injuries"],
        ["10_oshaIncidents", "osha_incidents"],
        ["11_ownManHours", "own_man_hours"],
        ["12_contractManHours", "contract_man_hours"],
      ];

      quantMap.forEach(([jsKey, apiBase]) => {
        // backend may store keys like `${apiBase}_2024` and `${apiBase}_2023`
        const y24 =
          pick(
            `${apiBase}_2024`,
            `${apiBase}2024`,
            `${apiBase}_24`,
            `${apiBase}_twenty24`
          ) ?? pick(`${jsKey}_2024`);
        const y23 =
          pick(
            `${apiBase}_2023`,
            `${apiBase}2023`,
            `${apiBase}_23`,
            `${apiBase}_twenty23`
          ) ?? pick(`${jsKey}_2023`);
        newFields[jsKey] = [
          y24 != null ? String(y24) : "",
          y23 != null ? String(y23) : "",
        ];
      });

      // ─── Project blocks (1..5)
      for (let i = 1; i <= 5; i++) {
        const jsPrefix = `project${i}_`;
        const apiPrefix = `project${i}_`;
        // map backend snake_case to your camelCase form keys
        newFields[`${jsPrefix}name`] =
          pick(
            `${apiPrefix}name`,
            `${apiPrefix}title`,
            `${apiPrefix}project_name`
          ) || "";
        // board approval date -> project{i}_boardApprovalDate
        newFields[`${jsPrefix}boardApprovalDate`] =
          pick(
            `${apiPrefix}board_approval`,
            `${apiPrefix}boardApproval`,
            `${apiPrefix}board_approval_date`
          ) || "";
        newFields[`${jsPrefix}startDate`] =
          pick(`${apiPrefix}start_date`, `${apiPrefix}startDate`) || "";
        newFields[`${jsPrefix}completionDate`] =
          pick(`${apiPrefix}completion_date`, `${apiPrefix}completionDate`) ||
          "";
        newFields[`${jsPrefix}capex`] =
          pick(
            `${apiPrefix}capex`,
            `${apiPrefix}_capex`,
            `${apiPrefix}Capex`
          ) || "";
        newFields[`${jsPrefix}remarks`] =
          pick(
            `${apiPrefix}remarks`,
            `${apiPrefix}note`,
            `${apiPrefix}_remarks`
          ) || "";
      }

      // ─── Attachments (1..4) - store description and keep file null, store url if provided
      for (let n = 1; n <= 4; n++) {
        const desc =
          pick(
            `attachments${n}_desc`,
            `attachments${n}_description`,
            `attachment${n}_desc`,
            `attachment${n}_description`,
            `attachments_${n}_desc`
          ) || "";
        // possible url / file fields
        const urlField =
          pick(
            `attachments${n}_url`,
            `attachments${n}_file_url`,
            `attachments${n}`,
            `attachment${n}`,
            `attachments_${n}_url`,
            `attachments${n}_file`
          ) || null;

        newFields[`attachments${n}`] = {
          description: desc,
          file: null,
          url: typeof urlField === "string" ? urlField : null,
        };
      }

      // Merge immutably into form state - preserve other keys that may exist
      setFormData((prev) => ({ ...prev, ...newFields }));

      console.log("Prefill applied, pkId:", _ResData.id);
    } catch (err) {
      console.error("prefillFormData error:", err);
    }
  }

  // UI State
  const [currentStep, setCurrentStep] = useState(1);
  const [copyApplicantData, setCopyApplicantData] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Validation helpers
  const validateEmail = (email) => emailRegex.test(email);
  const validatePhone = (phone) => phoneRegex.test(phone);

  // Clear error on field
  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (prev[field]) {
        const { [field]: omitted, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Validate field on blur
  const validateField = (name, value) => {
    let errorMsg = "";
    if (["Organisationname", "authorityName", "contact_name"].includes(name)) {
      if (!value.trim()) {
        errorMsg = `${name.replace(/([A-Z])/g, " $1").trim()} is required.`;
      } else if (!/^[A-Za-z\s]*$/.test(value)) {
        errorMsg = "Only letters and spaces are allowed.";
      }
    } else if (name === "mailingAddress" && !value.trim()) {
      errorMsg = "Mailing address is required.";
    } else if (name === "authorityEmail" || name === "contactEmail") {
      if (value && !validateEmail(value)) {
        errorMsg = `Please enter a valid ${
          name === "authorityEmail" ? "Authority" : "Contact"
        } email.`;
      } else if (name === "authorityEmail" && !value) {
        errorMsg = "Authority email is required.";
      }
    } else if (name === "authorityPhone" || name === "contactPhone") {
      if (value && !validatePhone(value)) {
        errorMsg = `${
          name === "authorityPhone" ? "Authority" : "Contact"
        } phone must be exactly 10 digits.`;
      } else if (name === "authorityPhone" && !value) {
        errorMsg = "Authority phone is required.";
      }
    } else if (name === "companyProfile" && !value.trim()) {
      errorMsg = "Company profile is required.";
    } else if (name === "awardJustification" && !value.trim()) {
      errorMsg = "Award justification is required.";
    } else if (name === "comment" && value.length > COMMENT_MAX_LENGTH) {
      errorMsg = `Comment must not exceed ${COMMENT_MAX_LENGTH} characters.`;
    } else if (name.includes("_remarks") && value.length > COMMENT_MAX_LENGTH) {
      errorMsg = `Remarks must not exceed ${COMMENT_MAX_LENGTH} characters.`;
    }
    return errorMsg;
  };

  // Handle blur for validation
  const handleBlur = (name, value) => {
    const errorMsg = validateField(name, value);
    if (errorMsg) {
      setFieldErrors((prev) => ({ ...prev, [name]: errorMsg }));
    } else {
      clearFieldError(name);
    }
  };

  // Handle changes for inputs/textareas/selects
  const handleChange = (name, value, index = null) => {
    if ([1, 2, 4].includes(currentStep)) {
      let applicableMaxLength = FIELD_MAX_LENGTH;
      if (name === "companyProfile" || name === "awardJustification")
        applicableMaxLength = COMPANY_PROFILE_MAX_LENGTH;
      else if (name === "comment" || name.includes("_remarks"))
        applicableMaxLength = COMMENT_MAX_LENGTH;

      if (typeof value === "string") {
        if (name === "companyProfile") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 300) {
            alert("Maximum 300 words allowed.");
            return;
          }
        } else if (name === "comment") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 200) {
            alert("Maximum 200 words allowed in Comments.");
            return;
          }
        } else if (value.length > applicableMaxLength) {
          alert(`Value cannot exceed ${applicableMaxLength} characters.`);
          return;
        }
      }
    }

    // Name validation
    if (["Organisationname", "authorityName", "contact_name"].includes(name)) {
      const isValid = /^[A-Za-z\s]*$/.test(value);
      if (!isValid && value !== "") {
        alert("Only letters and spaces are allowed.");
        return;
      }
    }

    // Quantitative fields validation
    const quantFields = [
      "1_totalOil",
      "2_totalGas",
      "3_costPerBOE",
      "4_iorEorCapex",
      "5_totalEnergy",
      "5.1_companyEnergy",
      "5.2_totalCapex",
      "5.3_totalOpex",
      "5.4_productionCapex",
      "5.5_productionOpex",
      "6_co2Emission",
      "8_fatalities",
      "9_lostTimeInjuries",
      "10_oshaIncidents",
      "11_ownManHours",
      "12_contractManHours",
      "project1_capex",
      "project2_capex",
      "project3_capex",
      "project4_capex",
      "project5_capex",
    ];
    if (quantFields.includes(name)) {
      if (index !== null) {
        const val = value.toString();
        if (val.startsWith("-") || val.toLowerCase().includes("e")) {
          alert("Negative numbers or scientific notation are not allowed.");
          return;
        }
        setFormData((prev) => {
          const updatedArray = [...prev[name]];
          updatedArray[index] = value;
          return { ...prev, [name]: updatedArray };
        });
        clearFieldError(`${name}[${index}]`);
      } else {
        if (value !== "" && (isNaN(value) || Number(value) < 0)) {
          alert("Value must be a non-negative number.");
          return;
        }
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
        clearFieldError(name);
      }
      return;
    }

    // Phone validation
    if (["authorityPhone", "contactPhone"].includes(name)) {
      const numericValue = value.replace(/\D/g, "").slice(0, PHONE_MAX_LENGTH);
      if (numericValue.length > PHONE_MAX_LENGTH) {
        alert("Phone number must not exceed 10 digits.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      clearFieldError(name);
      return;
    }

    // Default handling
    setFormData((prev) => ({
      ...prev,
      [name]: typeof value === "boolean" ? value : value || "",
    }));
    clearFieldError(name);

    if (
      ["Organisationname", "mailingAddress"].includes(name) &&
      value &&
      currentStep === 1
    )
      setError("");
    if (
      [
        "authorityName",
        "authorityTitle",
        "authorityEmail",
        "authorityPhone",
        "contact_name",
        "companyProfile",
        "awardJustification",
      ].includes(name) &&
      value &&
      currentStep === 2
    )
      setError("");
  };

  const handleCopyApplicantToggle = (e) => {
    const checked = e.target.checked;
    // setCopyApplicantData(checked);

    const model = {
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    };

    if (checked) {
      const userInfo = JSON.parse(sessionStorage.getItem("user_info"));
      if (userInfo) {
        model.contact_name = userInfo.applicant_name;
        model.contact_phone = userInfo.applicant_phone;
        model.contact_email = userInfo.email;
      }
    }

    setFormData((prev) => ({ ...prev, ...model }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
  };

  // Handle attachment changes
  const handleAttachmentChange = (key, field, value, event = null) => {
    if (field === "file" && value) {
      const file = value;
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for attachments.");
        if (event) event.target.value = null;
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for attachments.");
        if (event) event.target.value = null;
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
    clearFieldError(key);
  };

  // Handle approving authority signature upload
  const handleApprovingAuthorityChange = (files) => {
    const file = files[0];
    if (file) {
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for the signature.");
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for the signature.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        approvingAuthoritySignature: file,
      }));
      clearFieldError("approvingAuthoritySignature");
    }
  };

  // Quantitative data for steps 3 and 4
  const fullData = [
    ["1", "Total oil production during the year (MMT)", "1_totalOil"],
    ["2", "Total gas production during the year (BCM)", "2_totalGas"],
    ["3", "Cost of production ($/boe)", "3_costPerBOE"],
    [
      "4",
      "Capex in IOR / EOR projects implementation (INR Crore)",
      "4_iorEorCapex",
    ],
    ["5", "Total Energy (GJ) consumed in Production", "5_totalEnergy"],
    ["5.1", "Total Energy Consumed by the Company (GJ)", "5.1_companyEnergy"],
    ["5.2", "Total Capex of the Company (INR Crore)", "5.2_totalCapex"],
    ["5.3", "Total Opex of the Company (INR Crore)", "5.3_totalOpex"],
    ["5.4", "Capex for Production (INR Crore)", "5.4_productionCapex"],
    ["5.5", "Opex for Production (INR Crore)", "5.5_productionOpex"],
    [
      "6",
      "Total Carbon dioxide Emitted (Tonne) in Production",
      "6_co2Emission",
    ],
    ["7", "No. of Fatalities", "8_fatalities"],
    ["8", "Number of lost time injuries", "9_lostTimeInjuries"],
    ["9", "Number of OSHA recordable incidents", "10_oshaIncidents"],
    ["10", "Total Man Hours Worked (Own Employees)", "11_ownManHours"],
    [
      "11",
      "Total Man Hours Worked (Contractual Employees)",
      "12_contractManHours",
    ],
  ];

  const part1 = fullData.filter(([num]) => parseFloat(num) <= 7);
  const part2 = fullData.filter(([num]) => parseFloat(num) >= 8);

  // Print form handler
  const handlePrint = () => {
    const submissionDate = new Date("2025-08-06T00:20:00+05:30").toLocaleString(
      "en-US",
      {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: "Asia/Kolkata",
      }
    );

    const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${awardTitle || "Submission Details"}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          table { border-collapse: collapse; width: 100%; margin-top: 16px; }
          th, td { border: 1px solid #000; padding: 8px; }
          h1, h2 { margin-top: 20px; }
        </style>
      </head>
      <body>
        <h1 style="text-align: center; color: #1e40af;">${awardTitle || ""}</h1>

        <h2>Organization & Contact Details</h2>
        <p><strong>Organisation Name:</strong> ${
          formData.Organisationname || ""
        }</p>
        <p><strong>Postal Address:</strong> ${formData.mailingAddress || ""}</p>
        <p><strong>Approving Authority Name:</strong> ${
          formData.authorityName || ""
        }</p>
        <p><strong>Approving Authority Designation:</strong> ${
          formData.authorityTitle || ""
        }</p>
          <p><strong>Approving Authority Landline:</strong> ${
            formData.authorityLandline || ""
          }</p>
        <p><strong>Approving Authority Phone:</strong> ${
          formData.authorityPhone || ""
        }</p>
        <p><strong>Approving Authority Email:</strong> ${
          formData.authorityEmail || ""
        }</p>

        <h2>Nodal Official Contact Details:</h2>
        <p><strong>Contact Name:</strong> ${formData.contact_name || ""}</p>
        <p><strong>Contact Phone:</strong> ${formData.contact_phone || ""}</p>
        <p><strong>Contact Email:</strong> ${formData.contact_email || ""}</p>

        <p><strong>Brief write up on company profile and the activities with specific reference to
                Development & Production operations in 2024 -25:</strong> ${
                  formData.companyProfile || ""
                }</p>

        <h2>Quantitative Information - Part 1</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Particulars</th>
              <th>2024-25</th>
              <th>2023-24</th>
            </tr>
          </thead>
          <tbody>
            ${part1
              .map(
                ([num, label, key]) => `
              <tr>
                <td>${num}</td>
                <td>${label}</td>
                <td>${
                  Array.isArray(formData[key]) ? formData[key][0] || "" : ""
                }</td>
                <td>${
                  Array.isArray(formData[key]) ? formData[key][1] || "" : ""
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <h2>Quantitative Information - Part 2</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Particulars</th>
              <th>2024-25</th>
              <th>2023-24</th>
            </tr>
          </thead>
          <tbody>
            ${part2
              .map(
                ([num, label, key]) => `
              <tr>
                <td>${num}</td>
                <td>${label}</td>
                <td>${
                  Array.isArray(formData[key]) ? formData[key][0] || "" : ""
                }</td>
                <td>${
                  Array.isArray(formData[key]) ? formData[key][1] || "" : ""
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <h2>New Projects</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Project Name</th>
              <th>Board Approval Date</th>
              <th>Start Date</th>
              <th>Completion Date</th>
              <th>Capex (INR Crore)</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${[1, 2, 3, 4, 5]
              .map(
                (i) => `
              <tr>
                <td>${i}</td>
                <td>${formData[`project${i}_name`] || ""}</td>
                <td>${formData[`project${i}_boardApprovalDate`] || ""}</td>
                <td>${formData[`project${i}_startDate`] || ""}</td>
                <td>${formData[`project${i}_completionDate`] || ""}</td>
                <td>${formData[`project${i}_capex`] || ""}</td>
                <td>${formData[`project${i}_remarks`] || ""}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <h2>Attachments</h2>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Description</th>
              <th>File Name</th>
            </tr>
          </thead>
          <tbody>
            ${[1, 2, 3, 4]
              .map(
                (i) => `
              <tr>
                <td>${i}</td>
                <td>${formData[`attachments${i}`]?.description || ""}</td>
                <td>${formData[`attachments${i}`]?.file?.name || ""}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <h2>Comments</h2>
        <p>${formData.comment || ""}</p>

        <h2>Declaration</h2>
        <p>I declare that the information submitted is true and complete.</p>
        <br/>
        <p><strong>Approving Authority Name:</strong> ${
          formData.authorityName || ""
        }</p>
        <p><strong>Approving Authority Designation:</strong> ${
          formData.authorityTitle || ""
        }</p>
        <p><strong>Signed on:</strong> ${
          formData.signatureDate || new Date().toLocaleDateString()
        }</p>
        <p><strong>Approving Authority Signature:</strong></p>
      </body>
    </html>
  `;

    const printWindow = window.open("", "_blank", "height=800,width=1000");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  // Check for empty fields in a step
  const hasEmptyFieldsInStep3 = () => {
    for (const [, , key] of part1) {
      // ⛔️ Skip validation if the field is currently disabled
      if (shouldDisableField(key)) continue;

      const arr = formData[key];
      if (
        !arr ||
        arr.some((val) => val === "" || val === undefined || val === null)
      ) {
        return true;
      }
    }
    return false;
  };

  const hasEmptyFieldsInStep4 = () => {
    for (const [, , key] of part2) {
      const arr = formData[key];
      if (
        !arr ||
        arr.some((val) => val === "" || val === undefined || val === null)
      ) {
        return true;
      }
    }
    for (let i = 1; i <= 5; i++) {
      const requiredFields = [
        `project${i}_name`,
        `project${i}_boardApprovalDate`,
        `project${i}_startDate`,
        `project${i}_completionDate`,
        `project${i}_capex`,
      ];
      for (const field of requiredFields) {
        if (!formData[field] && formData[field] !== "0") {
          return true;
        }
      }
    }
    return false;
  };

  // Navigation handlers
  const nextStep = () => {
    const errors = {};
    if (currentStep === 1) {
      if (!formData.mailingAddress?.trim()) {
        errors.mailingAddress = "Mailing address is required.";
      }
    }
    if (currentStep === 2) {
      if (!formData.authorityName) {
        errors.authorityName = "Authority name is required.";
      }
      if (!formData.authorityTitle) {
        errors.authorityTitle = "Authority designation is required.";
      }
      if (!formData.authorityEmail || !validateEmail(formData.authorityEmail)) {
        errors.authorityEmail = "Please enter a valid Authority email.";
      }
      // if (!formData.authorityPhone || !validatePhone(formData.authorityPhone)) {
      //   errors.authorityPhone = 'Authority phone must be exactly 10 digits.';
      // }
      if (!formData.authorityLandline) {
        alert("Authority Landline is required.");
        return;
      }
      if (!formData.contact_name) {
        errors.contact_name = "Contact name is required.";
      }
      if (formData.contact_email && !validateEmail(formData.contactEmail)) {
        errors.contact_email = "Please enter a valid Contact email.";
      }
      if (
        formData.contact_phone &&
        !validatePhone(formData.contactPhone) &&
        !copyApplicantData
      ) {
        errors.contact_phone = "Contact phone must be exactly 10 digits.";
      }
      if (!formData.companyProfile.trim()) {
        errors.companyProfile = "Company profile is required.";
      }
    }
    if (currentStep === 3 && hasEmptyFieldsInStep3()) {
      if (!window.confirm("Data not entered, If you wish to continue?")) {
        return;
      }
    }
    if (currentStep === 4 && hasEmptyFieldsInStep4()) {
      if (!window.confirm("Data not entered, If you wish to continue?")) {
        return;
      }
    }

    setError("");
    if (currentStep < 5) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

     const viewFile =  async (serverPath) => {
      // "https://award-backend.fipi.org.in/media/
      const extractPath = serverPath.split('/').filter(
    (val) =>
      val !== "" && // remove empty strings
      val !== "http:" &&
      val !== "https:" &&
      val !== "media" &&
      !val.startsWith("award") &&
      !val.startsWith("localhost")
  ).join('/');
      
      downloadFile(extractPath)
    }
  // Save draft handler
  const handleSaveDraft = () => {
    localStorage.setItem(
      "registrationProductionlessMMTOEDraft",
      JSON.stringify({ formData })
    );
    handleSubmit(null);
    alert("Draft Saved!");
  };

  //Final Submit button

  const finalSubmit = () => {
    handleSubmit(null, "submited");
  };

  // Submit form handler
  // Replace your existing handleSubmit with this
  async function handleSubmit(event, clickType = "draft") {
    if (event) {
      event?.preventDefault();
    }
    console.log(
      "############################ Save the DAta on Server ##########################"
    );
    // 1) Make a local copy of formData so we don't rely on async state updates
    let payload = { ...formData };

    try {
      const raw = localStorage.getItem("user_info");
      if (raw) {
        const u = JSON.parse(raw);
        payload.userid = u.id ?? payload.userid;
        payload.firstname = u.first_name ?? payload.firstname;
        payload.lastname = u.last_name ?? payload.lastname;
        payload.company_name = u.organisation_name ?? payload.company_name;
        payload.Organisationname =
          u.organisation_name ?? payload.Organisationname;
      }
    } catch (err) {
      console.warn("Failed to load user_info:", err);
    }

    // ─── 1) Must accept the declaration

    // (Optional) You can add phone validation here if required

    // ─── 2) Build FormData
    const fd = new FormData();

    // Required top-level fields (keeps your original mapping)
    fd.append("organisation_name", formData.Organisationname || "");
    fd.append("firstname", formData.firstname || "");
    fd.append("lastname", formData.lastname || "");
    fd.append("category", formData.category || "");
    fd.append("mailing_address", formData.mailingAddress || "");
    fd.append("authority_name", formData.authorityName || "");
    fd.append("authorityLandline", formData.authorityLandline || "");
    fd.append("authority_title", formData.authorityTitle || "");
    fd.append("authority_email", formData.authorityEmail || "");
    fd.append("authority_phone", formData.authorityPhone || "");
    fd.append("form_id", formId);
    fd.append("form_mode", clickType);
    // Add Table Pk Id;
    if (PkId > 0) fd.append("id", PkId);
    // Signature file
    if (formData.approvingAuthoritySignature instanceof File) {
      fd.append(
        "approving_authority_file",
        formData.approvingAuthoritySignature
      );
    } else {
      // append empty string if none (optional)
      fd.append("approving_authority_file", "");
    }

    // Flat field mapping (JS key → API key)
    const flatFields = {
      Firstname: "firstname",
      Lastname: "lastname",
      userid: "userid",
      companyName: "company_name",
      contact_name: "contact_name",
      contact_phone: "contact_phone",
      contact_email: "contact_email",
      companyProfile: "company_profile",
      awardJustification: "award_justification",
      comment: "comment",
      declaration: "declaration",
    };
    Object.entries(flatFields).forEach(([jsKey, apiKey]) => {
      let val = formData[jsKey];
      if (jsKey === "declaration") {
        val = formData.declaration ? "true" : "false";
      }
      // only append if not undefined (DRF will accept blank strings)
      if (val !== undefined) {
        fd.append(apiKey, val);
      }
    });

    // ─── 3) Quantitative pairs (2024 & 2023)
    const quantMap = [
      ["1_totalOil", "total_oil"],
      ["2_totalGas", "total_gas"],
      ["3_costPerBOE", "cost_per_boe"],
      ["4_iorEorCapex", "ior_eor_capex"],
      ["5_totalEnergy", "total_energy"],
      ["5.1_companyEnergy", "company_energy"],
      ["5.2_totalCapex", "total_capex"],
      ["5.3_totalOpex", "total_opex"],
      ["5.4_productionCapex", "production_capex"],
      ["5.5_productionOpex", "production_opex"],
      ["6_co2Emission", "co2_emission"],
      ["8_fatalities", "fatalities"],
      ["9_lostTimeInjuries", "lost_time_injuries"],
      ["10_oshaIncidents", "osha_incidents"],
      ["11_ownManHours", "own_man_hours"],
      ["12_contractManHours", "contract_man_hours"],
    ];
    quantMap.forEach(([jsKey, apiBase]) => {
      const [y24 = "", y23 = ""] = formData[jsKey] || [];
      fd.append(`${apiBase}_2024`, y24);
      fd.append(`${apiBase}_2023`, y23);
    });

    // ─── 4) Project blocks
    for (let i = 1; i <= 5; i++) {
      const prefix = `project${i}_`;
      const mapping = {
        name: `project${i}_name`,
        boardApprovalDate: `project${i}_board_approval`,
        startDate: `project${i}_start_date`,
        completionDate: `project${i}_completion_date`,
        capex: `project${i}_capex`,
        remarks: `project${i}_remarks`,
      };
      Object.entries(mapping).forEach(([jsField, apiKey]) => {
        const val = formData[prefix + jsField];
        if (val !== undefined && val !== null) fd.append(apiKey, val);
      });
    }

    // ─── 5) Attachments
    [1, 2, 3, 4].forEach((n) => {
      const slot = formData[`attachments${n}`] || {};
      fd.append(`attachments${n}_desc`, slot.description || "");
      if (slot.file instanceof File) {
        fd.append(`attachments${n}`, slot.file);
      }
    });

    // ─── 6) Submit the FormData to API
    // try {
    //   await apiClient.post("/production/", fd);

    try {
      const response = await apiClient.post("/production/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // When User save the Data It will Prefill the form data and fill pkId
      prefillFormData();
    } catch (err) {
      console.error("Production registration submission error:", err);
      const serverMsg =
        err?.response?.data ?? err?.message ?? JSON.stringify(err);
      alert("Submission failed: " + JSON.stringify(serverMsg));
    }
  }

  const shouldDisableField = (key) => {
    const val5 = formData["5_totalEnergy"];
    const hasVal5 = Array.isArray(val5) && (val5[0] || val5[1]);

    const subKeys = [
      "5.1_companyEnergy",
      "5.2_totalCapex",
      "5.3_totalOpex",
      "5.4_productionCapex",
      "5.5_productionOpex",
    ];

    const hasSubVals = subKeys.some((subKey) => {
      const val = formData[subKey];
      return Array.isArray(val) && (val[0] || val[1]);
    });

    // If main has value, disable subs
    if (hasVal5 && subKeys.includes(key)) return true;

    // If any sub has value, disable main
    if (hasSubVals && key === "5_totalEnergy") return true;

    return false;
  };

  // Rendering steps content
  const renderStepContent = () => {
    const progress = ((currentStep - 1) / 4) * 100;
    const years = ["2024–25", "2023–24"];

    return (
      <div
        className="form-step"
        role="region"
        aria-label={`Step ${currentStep} of 5`}
      >
        <div
          className="progress-bar"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div className="progress" style={{ width: `${progress}%` }} />
        </div>

        {currentStep === 1 && (
          <div>
            <h3 className="step-title">Step 1: Organization Details</h3>
            <div className="form-group">
              <label htmlFor="Organisationname">
                Organisation Name:
                {/* <span aria-hidden="true" className="text-red">*</span> */}
              </label>
              <input
                id="Organisationname"
                name="Organisationname"
                type="text"
                maxLength={FIELD_MAX_LENGTH}
                value={formData.Organisationname}
                // onChange={(e) => handleChange('Organisationname', e.target.value)}
                disabled={true}
                onBlur={(e) => handleBlur("Organisationname", e.target.value)}
                aria-describedby="Organisationname-error"
                className={`form-input ${
                  fieldErrors.Organisationname ? "has-error" : ""
                }`}
                placeholder="Enter organisation name"
                required
                aria-required="true"
              />
              {fieldErrors.Organisationname && (
                <span
                  className="error-tooltip"
                  id="Organisationname-error"
                  role="alert"
                >
                  {fieldErrors.Organisationname}
                </span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="mailingAddress">
                Postal Address:{" "}
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <textarea
                id="mailingAddress"
                name="mailingAddress"
                value={formData.mailingAddress}
                onChange={(e) => handleChange("mailingAddress", e.target.value)}
                onBlur={(e) => handleBlur("mailingAddress", e.target.value)}
                className={`form-textarea ${
                  fieldErrors.mailingAddress ? "has-error" : ""
                }`}
                rows={3}
                maxLength={FIELD_MAX_LENGTH}
                placeholder="Enter postal address"
                aria-required="true"
                aria-describedby="mailingAddress-error"
              />
              {fieldErrors.mailingAddress && (
                <span
                  className="error-tooltip"
                  id="mailingAddress-error"
                  role="alert"
                >
                  {fieldErrors.mailingAddress}
                </span>
              )}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <h3 className="step-title">
              Step 2: Approving Authority & Contact
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section
                aria-labelledby="approving-authority-heading"
                className="step-section"
              >
                <h4 id="approving-authority-heading">Approving Authority</h4>
                <p className="note">
                  Approving authority should be concerned Director /Board level
                  executive.{" "}
                </p>
                <div className="form-group">
                  <label htmlFor="authorityName">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityName"
                    name="authorityName"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityName}
                    onChange={(e) =>
                      handleChange("authorityName", e.target.value)
                    }
                    onBlur={(e) => handleBlur("authorityName", e.target.value)}
                    aria-describedby="authorityName-error"
                    placeholder="Name"
                    className={`form-input ${
                      fieldErrors.authorityName ? "has-error" : ""
                    }`}
                    required
                    aria-required="true"
                  />
                  {fieldErrors.authorityName && (
                    <span
                      className="error-tooltip"
                      id="authorityName-error"
                      role="alert"
                    >
                      {fieldErrors.authorityName}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityTitle">
                    Designation:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityTitle"
                    name="authorityTitle"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityTitle}
                    onChange={(e) =>
                      handleChange("authorityTitle", e.target.value)
                    }
                    onBlur={(e) => handleBlur("authorityTitle", e.target.value)}
                    aria-describedby="authorityTitle-error"
                    placeholder="Designation"
                    className={`form-input ${
                      fieldErrors.authorityTitle ? "has-error" : ""
                    }`}
                    required
                    aria-required="true"
                  />
                  {fieldErrors.authorityTitle && (
                    <span
                      className="error-tooltip"
                      id="authorityTitle-error"
                      role="alert"
                    >
                      {fieldErrors.authorityTitle}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Landline:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityLandline"
                    type="number"
                    name="authorityLandline"
                    value={formData.authorityLandline}
                    onChange={(e) =>
                      handleChange("authorityLandline", e.target.value)
                    }
                    // onBlur={(e) => handleBlur('authorityLandline', e.target.value)}
                    className={`form-input ${
                      !formData.authorityLandline && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Landline number"
                    aria-required="true"
                    aria-describedby={
                      fieldErrors.authorityLandline
                        ? "authorityLandline-error"
                        : undefined
                    }
                  />
                  {!formData.authorityLandline && currentStep === 2 && (
                    <span className="error-tooltip">
                      Authority Landline Number is required
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Mobile:
                    {/* <span className="text-red" aria-hidden="true"></span> */}
                  </label>
                  <input
                    id="authorityPhone"
                    type="tel"
                    name="authorityPhone"
                    value={formData.authorityPhone}
                    onChange={(e) =>
                      handleChange("authorityPhone", e.target.value)
                    }
                    // onBlur={(e) => handleBlur('authorityPhone', e.target.value)}
                    className={`form-input`}
                    placeholder="Phone number"
                    maxLength={PHONE_MAX_LENGTH}
                    aria-required="true"
                    // aria-describedby={fieldErrors.authorityPhone ? 'authorityPhone-error' : undefined}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="authorityEmail">
                    Email:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityEmail"
                    name="authorityEmail"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityEmail}
                    onChange={(e) =>
                      handleChange("authorityEmail", e.target.value)
                    }
                    onBlur={(e) => handleBlur("authorityEmail", e.target.value)}
                    aria-describedby="authorityEmail-error"
                    placeholder="Approving authority's email"
                    className={`form-input ${
                      fieldErrors.authorityEmail ? "has-error" : ""
                    }`}
                    required
                    aria-required="true"
                  />
                  {fieldErrors.authorityEmail && (
                    <span
                      className="error-tooltip"
                      id="authorityEmail-error"
                      role="alert"
                    >
                      {fieldErrors.authorityEmail}
                    </span>
                  )}
                </div>
              </section>
              <section
                aria-labelledby="contacts-heading"
                className="step-section"
              >
                <h4 id="contacts-heading">
                  Contacts (Nodal Officials){" "}
                  <span aria-hidden="true" className="text-red">
                    *
                  </span>
                </h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="copyApplicantData"
                      // checked={copyApplicantData}
                      onChange={handleCopyApplicantToggle}
                      className="form-checkbox"
                    />{" "}
                    Same as applicant
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_name">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_name}
                    onChange={(e) =>
                      handleChange("contact_name", e.target.value)
                    }
                    placeholder="Contact name"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      !formData.contact_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    aria-describedby="contact_name-error"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_phone">
                    Phone:
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formData.contact_phone}
                    onChange={(e) =>
                      handleChange("contact_phone", e.target.value)
                    }
                    aria-describedby="contact_phone-error"
                    disabled={copyApplicantData}
                    placeholder="10-digit phone number"
                    className={`form-input ${
                      !formData.contact_phone && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_email">Email:</label>
                  <input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_email}
                    onChange={(e) =>
                      handleChange("contact_email", e.target.value)
                    }
                    placeholder="Contact email"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      fieldErrors.contact_email ? "has-error" : ""
                    }`}
                    aria-describedby="contact_email-error"
                  />
                </div>
              </section>
            </div>
            <div className="form-group">
              <label htmlFor="companyProfile">
                Brief write up on company profile and the activities with
                specific reference to Development & Production operations in
                2024 -25.{" "}
              </label>
              <p className="note">(within 300 words)</p>
              <textarea
                id="companyProfile"
                name="companyProfile"
                value={formData.companyProfile}
                // maxLength={COMPANY_PROFILE_MAX_LENGTH}
                onChange={(e) => handleChange("companyProfile", e.target.value)}
                onBlur={(e) => handleBlur("companyProfile", e.target.value)}
                className={`form-textarea ${
                  fieldErrors.companyProfile ? "has-error" : ""
                }`}
                rows={6}
                aria-describedby="companyProfile-error"
                required
                aria-required="true"
              />
              {fieldErrors.companyProfile && (
                <span
                  className="error-tooltip"
                  id="companyProfile-error"
                  role="alert"
                >
                  {fieldErrors.companyProfile}
                </span>
              )}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div>
            <h3 className="step-title">
              Step 3: Quantitative Information Part 1
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Particulars</th>
                  <th scope="col">2024–25</th>
                  <th scope="col">2023–24</th>
                </tr>
              </thead>
              <tbody>
                {part1.map(([num, label, key]) => (
                  <tr key={key}>
                    <td>{num}</td>
                    <td>{label}</td>
                    {[0, 1].map((index) => (
                      <td key={index}>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="text" // use text to avoid number input spinners
                          value={
                            Array.isArray(formData[key])
                              ? formData[key][index] || ""
                              : ""
                          }
                          onChange={(e) => {
                            let val = e.target.value;

                            // Allow only digits and one decimal point
                            val = val.replace(/[^0-9.]/g, "");
                            const parts = val.split(".");
                            if (parts.length > 2) {
                              // keep only the first decimal point
                              val = parts[0] + "." + parts.slice(1).join("");
                            }

                            handleChange(key, val, index);
                          }}
                          onBlur={(e) =>
                            handleBlur(`${key}[${index}]`, e.target.value)
                          }
                          className={`form-input no-spinner ${
                            fieldErrors[`${key}[${index}]`] ? "has-error" : ""
                          }`}
                          aria-describedby={`${key}-${index}-error`}
                          disabled={shouldDisableField(key)}
                          sx={{
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor: "#f5f5f5",
                              color: "rgba(0,0,0,0.6)",
                            },
                          }}
                          onKeyDown={(e) => {
                            // block invalid keys
                            if (
                              ["-", "+", "e", "E"].includes(e.key) ||
                              e.key === "ArrowUp" ||
                              e.key === "ArrowDown"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()} // disable scroll increment
                          aria-label={`${label} for ${years[index]}`}
                          inputProps={{
                            inputMode: "decimal", // mobile decimal keypad
                            pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          }}
                        />

                        {fieldErrors[`${key}[${index}]`] && (
                          <span
                            className="error-tooltip"
                            id={`${key}-${index}-error`}
                            role="alert"
                          >
                            {fieldErrors[`${key}[${index}]`]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {currentStep === 4 && (
          <div>
            <h3 className="step-title">
              Step 4: Quantitative Information Part 2 & New Projects
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Particulars</th>
                  <th scope="col">2024–25</th>
                  <th scope="col">2023–24</th>
                </tr>
              </thead>
              <tbody>
                {part2.map(([num, label, key]) => (
                  <tr key={key}>
                    <td>{num}</td>
                    <td>{label}</td>
                    {[0, 1].map((index) => (
                      <td key={index}>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="text" // use text to avoid browser number spinners
                          value={
                            Array.isArray(formData[key])
                              ? formData[key][index] || ""
                              : ""
                          }
                          onChange={(e) => {
                            let val = e.target.value;

                            // ✅ Allow only digits and a single decimal point
                            val = val.replace(/[^0-9.]/g, "");
                            const parts = val.split(".");
                            if (parts.length > 2) {
                              val = parts[0] + "." + parts.slice(1).join("");
                            }

                            handleChange(key, val, index);
                          }}
                          onBlur={(e) =>
                            handleBlur(`${key}[${index}]`, e.target.value)
                          }
                          className={`form-input no-spinner ${
                            fieldErrors[`${key}[${index}]`] ? "has-error" : ""
                          }`}
                          aria-describedby={`${key}-${index}-error`}
                          onKeyDown={(e) => {
                            // block invalid characters and arrows
                            if (
                              ["-", "+", "e", "E"].includes(e.key) ||
                              e.key === "ArrowUp" ||
                              e.key === "ArrowDown"
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                          aria-label={`${label} for ${years[index]}`}
                          inputProps={{
                            inputMode: "decimal", // numeric keypad on mobile
                            pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          }}
                        />
                        {fieldErrors[`${key}[${index}]`] && (
                          <span
                            className="error-tooltip"
                            id={`${key}-${index}-error`}
                            role="alert"
                          >
                            {fieldErrors[`${key}[${index}]`]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <table className="quant-table">
              <thead>
                <tr>
                  <th colSpan="1">12</th>
                  <th colSpan="7">
                    <p className="tablerowq">
                      New Projects initiated in Assessment year to augment
                      production (Board Approved)
                    </p>
                  </th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Project Name</th>
                  <th scope="col">Board Approval Date</th>
                  <th scope="col">Start Date</th>
                  <th scope="col">Schedule Completion Date</th>
                  <th scope="col">Total Envisaged Capex (INR Crore)</th>
                  <th scope="col">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td>{i}</td>
                    <td>
                      <input
                        type="text"
                        name={`project${i}_name`}
                        maxLength={FIELD_MAX_LENGTH}
                        value={formData[`project${i}_name`]}
                        onChange={(e) =>
                          handleChange(`project${i}_name`, e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(`project${i}_name`, e.target.value)
                        }
                        className={`form-input ${
                          fieldErrors[`project${i}_name`] ? "has-error" : ""
                        }`}
                        aria-describedby={`project${i}_name-error`}
                        required
                        aria-required="true"
                      />
                      {fieldErrors[`project${i}_name`] && (
                        <span
                          className="error-tooltip"
                          id={`project${i}_name-error`}
                          role="alert"
                        >
                          {fieldErrors[`project${i}_name`]}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="date"
                        value={formData[`project${i}_boardApprovalDate`]}
                        onChange={(e) =>
                          handleChange(
                            `project${i}_boardApprovalDate`,
                            e.target.value
                          )
                        }
                        onBlur={(e) =>
                          handleBlur(
                            `project${i}_boardApprovalDate`,
                            e.target.value
                          )
                        }
                        className={`form-input ${
                          fieldErrors[`project${i}_boardApprovalDate`]
                            ? "has-error"
                            : ""
                        }`}
                        aria-describedby={`project${i}_boardApprovalDate-error`}
                        required
                        aria-required="true"
                      />
                      {fieldErrors[`project${i}_boardApprovalDate`] && (
                        <span
                          className="error-tooltip"
                          id={`project${i}_boardApprovalDate-error`}
                          role="alert"
                        >
                          {fieldErrors[`project${i}_boardApprovalDate`]}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="date"
                        value={formData[`project${i}_startDate`]}
                        onChange={(e) =>
                          handleChange(`project${i}_startDate`, e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(`project${i}_startDate`, e.target.value)
                        }
                        className={`form-input ${
                          fieldErrors[`project${i}_startDate`]
                            ? "has-error"
                            : ""
                        }`}
                        aria-describedby={`project${i}_startDate-error`}
                        required
                        aria-required="true"
                      />
                      {fieldErrors[`project${i}_startDate`] && (
                        <span
                          className="error-tooltip"
                          id={`project${i}_startDate-error`}
                          role="alert"
                        >
                          {fieldErrors[`project${i}_startDate`]}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="date"
                        value={formData[`project${i}_completionDate`]}
                        onChange={(e) =>
                          handleChange(
                            `project${i}_completionDate`,
                            e.target.value
                          )
                        }
                        onBlur={(e) =>
                          handleBlur(
                            `project${i}_completionDate`,
                            e.target.value
                          )
                        }
                        className={`form-input ${
                          fieldErrors[`project${i}_completionDate`]
                            ? "has-error"
                            : ""
                        }`}
                        aria-describedby={`project${i}_completionDate-error`}
                        required
                        aria-required="true"
                      />
                      {fieldErrors[`project${i}_completionDate`] && (
                        <span
                          className="error-tooltip"
                          id={`project${i}_completionDate-error`}
                          role="alert"
                        >
                          {fieldErrors[`project${i}_completionDate`]}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={formData[`project${i}_capex`] || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (
                            !val.startsWith("-") &&
                            !val.toLowerCase().includes("e")
                          ) {
                            handleChange(`project${i}_capex`, val);
                          }
                        }}
                        onBlur={(e) =>
                          handleBlur(`project${i}_capex`, e.target.value)
                        }
                        className={`form-input no-spinner ${
                          fieldErrors[`project${i}_capex`] ? "has-error" : ""
                        }`}
                        aria-describedby={`project${i}_capex-error`}
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                        }}
                        required
                        aria-required="true"
                      />
                      {fieldErrors[`project${i}_capex`] && (
                        <span
                          className="error-tooltip"
                          id={`project${i}_capex-error`}
                          role="alert"
                        >
                          {fieldErrors[`project${i}_capex`]}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        name={`project${i}_remarks`}
                        maxLength={COMMENT_MAX_LENGTH}
                        value={formData[`project${i}_remarks`]}
                        onChange={(e) =>
                          handleChange(`project${i}_remarks`, e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(`project${i}_remarks`, e.target.value)
                        }
                        className={`form-input ${
                          fieldErrors[`project${i}_remarks`] ? "has-error" : ""
                        }`}
                        aria-describedby={`project${i}_remarks-error`}
                      />
                      {fieldErrors[`project${i}_remarks`] && (
                        <span
                          className="error-tooltip"
                          id={`project${i}_remarks-error`}
                          role="alert"
                        >
                          {fieldErrors[`project${i}_remarks`]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="form-group">
              <label htmlFor="comment">Comments</label>
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                // maxLength={COMMENT_MAX_LENGTH}
                onChange={(e) => handleChange("comment", e.target.value)}
                onBlur={(e) => handleBlur("comment", e.target.value)}
                className={`form-textarea ${
                  fieldErrors.comment ? "has-error" : ""
                }`}
                placeholder="Comments in (200 words) against input parameter, if any"
                aria-describedby="comment-error"
              />
              {fieldErrors.comment && (
                <span className="error-tooltip" id="comment-error" role="alert">
                  {fieldErrors.comment}
                </span>
              )}
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div>
            <h3 className="step-title">Step 5: Attachments & Declaration</h3>
            <div className="form-group">
              <label>List of Attachments (Optional):</label>
              <table className="quant-table">
                <thead>
                  <tr>
                    <th scope="col">S.No</th>
                    <th scope="col">Description</th>
                    <th scope="col">
                      Upload (only jpg, png, pdf, max 5 MB each)
                    </th>
                  </tr>
                </thead>
                {/* <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            maxLength={FIELD_MAX_LENGTH}
                            value={formData[key].description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleBlur(`${key}.description`, e.target.value)
                            }
                            placeholder="Enter description"
                            className={`form-input ${
                              fieldErrors[`${key}.description`]
                                ? "has-error"
                                : ""
                            }`}
                            aria-describedby={`${key}-description-error`}
                          />
                          {fieldErrors[`${key}.description`] && (
                            <span
                              className="error-tooltip"
                              id={`${key}-description-error`}
                              role="alert"
                            >
                              {fieldErrors[`${key}.description`]}
                            </span>
                          )}
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                            aria-describedby={`${key}-file-error`}
                          />
                          {formData[key].file && (
                            <p className="file-name">
                              Selected file: {formData[key].file.name}
                            </p>
                          )}
                          {fieldErrors[`${key}.file`] && (
                            <span
                              className="error-tooltip"
                              id={`${key}-file-error`}
                              role="alert"
                            >
                              {fieldErrors[`${key}.file`]}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody> */}
                <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key] || {
                      description: "",
                      file: null,
                    };
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                          {attachment.url && !attachment.file && (
                            <p className="file-name">
                              Uploaded file:{" "}
                              <button
                                type="button"
                                onClick={() => viewFile(attachment.url)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#06c",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                View
                              </button>
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="form-group">
              <label>
                Print the completed application form and upload it with the
                signature of the approving authority. Without signature of the
                approving authority, application will not be considered valid.
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline"
                >
                  Print Preview
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="approvingAuthoritySignature">
                Upload Document with Approving Authority Signature
                (Director/Board Level)
                <span aria-hidden="true" className="text-red">
                  *
                </span>
                :
              </label>
              <input
                type="file"
                id="approvingAuthoritySignature"
                accept=".jpg,.png,.pdf"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files.length > 0) {
                    const confirmed = window.confirm(
                      "Are you sure the form is approved and signed by approving authority?"
                    );
                    if (!confirmed) {
                      // If user clicks "Cancel", clear the input
                      e.target.value = "";
                      return;
                    }
                    handleApprovingAuthorityChange(files);
                  }
                }}
                className={`form-input mt-4 ${
                  fieldErrors.approvingAuthoritySignature ? "has-error" : ""
                }`}
                aria-describedby="approvingAuthoritySignature-error"
                required
              />
              {formData.approvingAuthoritySignature && (
                <p className="file-name">
                  Selected file: {formData.approvingAuthoritySignature.name}
                </p>
              )}
              {formData.approvingAuthoritySignatureUrl &&
                !formData.approvingAuthoritySignature && (
                  <p className="file-name">
                    Uploaded file:{" "}
                    <button
                      type="button"
                      onClick={() =>
                        viewFile(formData.approvingAuthoritySignatureUrl)
                      }
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#06c",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  </p>
                )}
              {fieldErrors.approvingAuthoritySignature && (
                <span
                  className="error-tooltip"
                  id="approvingAuthoritySignature-error"
                  role="alert"
                >
                  {fieldErrors.approvingAuthoritySignature}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="declaration">
                <input
                  id="declaration"
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={(e) =>
                    handleChange("declaration", e.target.checked)
                  }
                  className={`form-checkbox ${
                    fieldErrors.declaration ? "has-error" : ""
                  }`}
                  aria-required="true"
                  aria-describedby="declaration-error"
                />
                I declare that the information submitted is true and complete.
              </label>
              {fieldErrors.declaration && (
                <span
                  className="error-tooltip"
                  id="declaration-error"
                  role="alert"
                >
                  {fieldErrors.declaration}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const middlePosition = window.innerHeight / 2; // middle
    window.scrollTo({ top: middlePosition + 100, behavior: "smooth" });
  }, [currentStep]);

  return (
    <div className="sidebar-guideline-container">
      <SidebarGuideline
        isOpen={true}
        sidebarItems={[{ id: "Guideline", label: "Guideline" }]}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        selectedAwardCategory={formData.category}
      />
      <div className="application-form">
        <div className="form-header">
          <h1>{awardTitle}</h1>
          <h6>Step {currentStep} of 5</h6>
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your submission!</h2>
            <p>Your Application has been successfully submitted.</p>
            <button onClick={() => navigate("/#Award%20Categories")}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            {currentStep === 1 && (
              <div className="form-navigation-step1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
            {currentStep > 1 && (
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                {currentStep < 5 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                )}
                {currentStep === 5 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isSubmited()}
                    onClick={(e) => {
                      if (!formData.declaration) {
                        return alert(
                          "Please accept the declaration before submitting."
                        );
                      }
                      if (!formData.approvingAuthoritySignature) {
                        return alert(
                          "Please upload the approving authority signature file."
                        );
                      }
                      if (!window.confirm("Are you sure you want to submit?")) {
                        e.preventDefault();
                      }
                      setIsSubmitted(true);
                      alert("Production registration submitted successfully!");

                      handleSubmit(e, "submit");
                    }}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationProductionlessMMTOE;
